import { useDeferredValue, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  useCreateSpecialQuotation,
  useUpdateSpecialTargets,
} from "@/features/quotation/hooks";
import { useStorePage } from "@/features/store/hooks";
import { useClientPage } from "@/features/client/hooks";
import type {
  DailyQuotation,
  QuotationTarget,
  QuotationTargetType,
} from "@/features/quotation/types";
import { isCurrentQuotationOperation } from "@/features/quotation/publication";
import { formatDateBR } from "@/lib/timeAgo";
import { TARGET_LABELS } from "./specialQuotationTargets";

export type SpecialDrawerState =
  | { mode: "create"; parent: DailyQuotation }
  | { mode: "edit"; parent: DailyQuotation; special: DailyQuotation };

const SPECIAL_TARGET_TYPES = ["customer", "store"] as const;
const TARGET_PAGE_SIZE = 100;

const labelStyle: React.CSSProperties = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "0.75rem",
  letterSpacing: "0.06em",
  color: "var(--login-muted)",
};

const btnPrimary: React.CSSProperties = {
  flex: 1,
  padding: "0.75rem",
  background: "var(--login-accent)",
  border: "none",
  borderRadius: "0.5rem",
  color: "#fff",
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  textTransform: "uppercase",
  fontSize: "1rem",
  letterSpacing: "0.06em",
  cursor: "pointer",
};

export function SpecialQuotationDrawer({
  state,
  onClose,
}: {
  state: SpecialDrawerState | null;
  onClose: () => void;
}) {
  const [targetType, setTargetType] = useState<QuotationTargetType>("customer");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<QuotationTarget[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const deferredQuery = useDeferredValue(query);
  const clientsQuery = useClientPage(
    { limit: TARGET_PAGE_SIZE, offset, search: deferredQuery },
    Boolean(state) && targetType === "customer",
  );
  const storesQuery = useStorePage(
    { limit: TARGET_PAGE_SIZE, offset, search: deferredQuery },
    Boolean(state) && targetType === "store",
  );
  const targetsQuery = targetType === "customer" ? clientsQuery : storesQuery;
  const targetsData = targetsQuery.data;
  const parent = state?.parent ?? null;
  const special = state?.mode === "edit" ? state.special : null;
  const isEdit = state?.mode === "edit";
  const createMutation = useCreateSpecialQuotation(parent?.id ?? "");
  const updateMutation = useUpdateSpecialTargets(special?.id ?? "", parent?.id);
  const isPending = createMutation.isPending || updateMutation.isPending;
  const stateKey = state
    ? `${state.parent.id}:${state.mode}:${state.mode === "edit" ? state.special.id : ""}`
    : "";
  const activeStateKey = useRef(stateKey);
  const activeParentId = useRef(parent?.id ?? "");
  const operation = useRef(0);
  activeParentId.current = parent?.id ?? "";
  if (activeStateKey.current !== stateKey) {
    activeStateKey.current = stateKey;
    operation.current += 1;
  }

  useEffect(() => {
    if (!state) return;
    setTargetType("customer");
    setQuery("");
    setOffset(0);
    setSelected(state.mode === "edit" ? (state.special.targets ?? []) : []);
    setError(null);
  }, [state]);

  const options = (targetsData?.items ?? []).map((target) => ({
    id: target.id,
    name: target.name,
    target_type: targetType,
  }));
  const visibleTargetKeys = new Set(
    options.map((option) => `${option.target_type}:${option.id}`),
  );
  const selectedOutsidePage = selected.filter(
    (target) =>
      !visibleTargetKeys.has(`${target.target_type}:${target.target_id}`),
  );

  function toggleTarget(option: {
    id: string;
    name: string;
    target_type: QuotationTargetType;
  }) {
    setError(null);
    setSelected((current) => {
      const exists = current.some(
        (target) =>
          target.target_id === option.id &&
          target.target_type === option.target_type,
      );
      if (exists) {
        return current.filter(
          (target) =>
            !(
              target.target_id === option.id &&
              target.target_type === option.target_type
            ),
        );
      }
      return [
        ...current,
        {
          target_id: option.id,
          target_type: option.target_type,
          name: option.name,
        },
      ];
    });
  }

  async function createSpecial() {
    if (!parent || isPending) return;
    if (selected.length === 0) {
      setError("Selecione pelo menos um cliente ou loja.");
      return;
    }
    setError(null);
    const generation = ++operation.current;
    const originalParentId = parent.id;
    const originalStateKey = stateKey;
    try {
      const payload = {
        targets: selected.map((target) => ({
          target_type: target.target_type,
          target_id: target.target_id,
        })),
      };
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        if (
          activeStateKey.current !== originalStateKey ||
          !isCurrentQuotationOperation({
            quotationId: originalParentId,
            activeQuotationId: activeParentId.current,
            generation,
            activeGeneration: operation.current,
          })
        )
          return;
        operation.current += 1;
        onClose();
        return;
      }
      const created = await createMutation.mutateAsync(payload);
      if (
        activeStateKey.current !== originalStateKey ||
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        })
      )
        return;
      operation.current += 1;
      onClose();
      navigate({ to: `/cotacoes/${created.id}` as string });
    } catch (e) {
      if (
        activeStateKey.current !== originalStateKey ||
        !isCurrentQuotationOperation({
          quotationId: originalParentId,
          activeQuotationId: activeParentId.current,
          generation,
          activeGeneration: operation.current,
        })
      )
        return;
      setError(
        getUserFacingErrorMessage(
          e,
          isEdit
            ? "Não foi possível salvar os alvos."
            : "Não foi possível criar a cotação especial.",
        ),
      );
    }
  }

  return (
    <AdminDrawer
      open={Boolean(state)}
      onClose={() => {
        if (isPending) return;
        operation.current += 1;
        onClose();
      }}
      closeDisabled={isPending}
      onSubmit={createSpecial}
      submitDisabled={isPending}
      mobilePresentation="centered"
      title={isEdit ? "Editar alvos" : "Cotação especial"}
    >
      <div className="quotation-special-drawer-content">
        <span style={{ ...labelStyle, color: "var(--login-text)" }}>
          {parent ? formatDateBR(parent.date) : ""}
        </span>

        <OperationalSearchField
          value={query}
          onChange={(value) => {
            setQuery(value);
            setOffset(0);
          }}
          placeholder={`Buscar ${TARGET_LABELS[targetType].toLowerCase()}...`}
          ariaLabel={`Buscar ${TARGET_LABELS[targetType].toLowerCase()}`}
        />

        <div style={{ display: "grid", gap: "0.45rem" }}>
          <span style={labelStyle}>Tipo</span>
          <OperationalSelect
            ariaLabel="Tipo de alvo"
            menuLayer="dialog"
            value={targetType}
            options={SPECIAL_TARGET_TYPES.map((type) => ({
              value: type,
              label: TARGET_LABELS[type],
            }))}
            onChange={(value) => {
              setTargetType(value as QuotationTargetType);
              setQuery("");
              setOffset(0);
            }}
          />
        </div>

        <div className="quotation-special-target-summary" aria-live="polite">
          <span>
            <strong>{selected.length}</strong> selecionados
          </span>
          <span>
            <strong>{targetsData?.total ?? 0}</strong>{" "}
            {targetType === "customer" ? "clientes" : "lojas"}
          </span>
        </div>

        <div
          className="quotation-special-target-list"
          style={{ display: "grid", gap: "0.4rem" }}
        >
          {selectedOutsidePage.map((target) => (
            <button
              key={`${target.target_type}:${target.target_id}`}
              type="button"
              className="quotation-special-target-row is-selected"
              aria-pressed="true"
              aria-label={`Remover ${target.name || target.target_id}`}
              onClick={() =>
                toggleTarget({
                  id: target.target_id,
                  name: target.name || "",
                  target_type: target.target_type,
                })
              }
            >
              <span className="quotation-special-target-copy">
                <strong>
                  {target.name ||
                    (target.target_type === "customer"
                      ? "Cliente indisponível"
                      : "Loja indisponível")}
                </strong>
                <small>Selecionado fora desta lista</small>
              </span>
              <CheckCircle2 size={18} aria-hidden="true" />
            </button>
          ))}
          {options.map((option) => {
            const active = selected.some(
              (target) =>
                target.target_id === option.id &&
                target.target_type === option.target_type,
            );
            return (
              <button
                key={`${option.target_type}-${option.id}`}
                type="button"
                className={`quotation-special-target-row ${active ? "is-selected" : ""}`}
                aria-pressed={active}
                onClick={() => toggleTarget(option)}
                style={{
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span className="quotation-special-target-copy">
                  <strong>{option.name}</strong>
                  <small>
                    {active ? "Receberá esta cotação" : "Não selecionado"}
                  </small>
                </span>
                {active ? <CheckCircle2 size={18} aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>

        {(targetsData?.total ?? 0) > TARGET_PAGE_SIZE ? (
          <OperationalPager
            totalItems={targetsData?.total ?? 0}
            limit={targetsData?.limit ?? TARGET_PAGE_SIZE}
            offset={targetsData?.offset ?? offset}
            isFetching={targetsQuery.isFetching}
            onPageChange={(page) => setOffset(page * TARGET_PAGE_SIZE)}
            ariaLabel={`Paginação de ${TARGET_LABELS[targetType].toLowerCase()}`}
            itemLabel={targetType === "customer" ? "cliente" : "loja"}
            itemLabelPlural={targetType === "customer" ? "clientes" : "lojas"}
            compact
          />
        ) : null}

        {error ? (
          <p
            role="alert"
            style={{
              margin: 0,
              color: "#ef4444",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </p>
        ) : null}

        <footer className="quotation-special-drawer-footer">
          <button
            type="button"
            onClick={createSpecial}
            disabled={isPending}
            style={{ ...btnPrimary, opacity: isPending ? 0.72 : 1 }}
          >
            {isPending
              ? isEdit
                ? "Salvando..."
                : "Criando..."
              : isEdit
                ? "Salvar"
                : "Criar"}
          </button>
        </footer>
      </div>
    </AdminDrawer>
  );
}
